/**
 * disposal_magic_link_tokens — Short-lived email-verification tokens (DEC-156).
 *
 * Phase 16 / Task 16.1 — quick 260715-a1x.
 *
 * Mirror of operator_magic_link_tokens (0059 / DEC-148). Stores SHA-256 hashes
 * of opaque magic-link tokens used to verify disposal registrant email
 * addresses. The RAW token is NEVER persisted — only the SHA-256 hex hash is
 * stored here.
 *
 * NO RLS: This table has no tenant context (the registrant is pre-tenant).
 * Same posture as operator_magic_link_tokens — the migration (0063) intentionally
 * does NOT REVOKE INSERT/UPDATE/DELETE from app_user; reads/writes go through
 * the platform-db plugin (see packages/api/src/plugins/platform-db.ts).
 *
 * Single-use enforcement: the consume service checks consumed_at IS NULL within
 * a DB transaction (SELECT ... FOR UPDATE) before marking consumed. There is no
 * UNIQUE constraint on consumed_at itself — the service-level guard is
 * sufficient and avoids false conflicts on NULL (NULL != NULL in SQL UNIQUE).
 *
 * Spec: DEC-156, mirror of DEC-148 §Decision 1 (token strategy).
 */

import { pgTable, uuid, char, timestamp, index } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { disposalRegistrations } from './disposal-registrations.js';

/**
 * disposal_magic_link_tokens — one row per issued magic link.
 *
 * token_hash: SHA-256 hex of the raw opaque token (64 chars). UNIQUE — no two
 * rows may have the same hash. Raw token = 32 bytes crypto.randomBytes →
 * base64url → 43 chars. DB compromise leaks only the hashes, not tokens.
 *
 * expires_at: 15 minutes from issue time (MAGIC_LINK_TTL_MS in the service).
 *
 * consumed_at: NULL until the token is consumed. The consume service sets this
 * in the same transaction that advances the registration to EMAIL_VERIFIED.
 */
export const disposalMagicLinkTokens = pgTable(
  'disposal_magic_link_tokens',
  {
    id: uuid('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    registrationId: uuid('registration_id')
      .notNull()
      .references(() => disposalRegistrations.id, { onDelete: 'cascade' }),
    // SHA-256 hex: always exactly 64 hex characters. UNIQUE enforced in migration.
    tokenHash: char('token_hash', { length: 64 }).notNull().unique(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    // NULL until consumed. Single-use enforced by the service (not a DB constraint).
    consumedAt: timestamp('consumed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Supports loading all tokens for a registration (e.g. cleanup on expiry sweep).
    index('disposal_magic_link_tokens_registration_id_idx').on(t.registrationId),
  ],
);

export type DisposalMagicLinkToken = typeof disposalMagicLinkTokens.$inferSelect;
export type NewDisposalMagicLinkToken = typeof disposalMagicLinkTokens.$inferInsert;
